import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { NO_DEPARTMENT } from '../../../constants/organizations';
import useWindowSize from '../../../hooks/useWindowSize';
import { selectUserDepartmentId } from '../../../store/selectors/auth';
import { selectCurrentOrganizationDepartmentList } from '../../../store/selectors/organizations';
import { TCollectiveTemplateItem } from '../../../types/templates';
import { getDepartmentLabelById } from '../../../utils/users';
import { DropdownWithContent } from '../../index';

interface IUseTemplateDepartmentsReturn {
  getDepartmentLabel: (id: string) => string;
  renderDefaultLabel: (isDefault: boolean, styles: Record<string, string>) => JSX.Element | null;
  getDepartmentsInfo: (template: TCollectiveTemplateItem, styles: Record<string, string>) => JSX.Element;
}

export const useTemplateDepartments = (): IUseTemplateDepartmentsReturn => {
  const { mobile } = useWindowSize();
  const userDepartmentId = useSelector(selectUserDepartmentId);
  const departments = useSelector(selectCurrentOrganizationDepartmentList);

  const getDepartmentLabel = useCallback((id: string) => getDepartmentLabelById(id, departments) || '', [departments]);

  const renderDefaultLabel = useCallback((isDefault: boolean, styles: Record<string, string>) => {
    return isDefault ? <span className={styles.default}>Default</span> : null;
  }, []);

  const getDepartmentsInfo = useCallback(
    (template: TCollectiveTemplateItem, styles: Record<string, string>) => {
      const { department_ids: depIds, is_default = false } = template;

      const mainDept = (() => {
        if (depIds.includes(userDepartmentId)) {
          return userDepartmentId;
        }

        if (depIds.includes(NO_DEPARTMENT)) {
          return NO_DEPARTMENT;
        }

        return depIds[0];
      })();

      const additionalDeptIds = depIds.filter((id) => id !== mainDept);

      return (
        <div className={styles.deptInfo}>
          <span className={styles.mainDept}>
            {mainDept === NO_DEPARTMENT ? 'Global' : getDepartmentLabel(mainDept)}
          </span>
          {depIds.length > 1 && (
            <DropdownWithContent
              label={() => (
                <span className={styles.badge}>
                  {`+${additionalDeptIds.length} team${additionalDeptIds.length > 1 ? 's' : ''}`}
                </span>
              )}
              className={styles.additionalDept}
              contentClassName={styles.additionalDepartsList}
            >
              {() => (
                <React.Fragment>
                  {additionalDeptIds.map((id) => (
                    <div key={id}>{getDepartmentLabel(id)}</div>
                  ))}
                </React.Fragment>
              )}
            </DropdownWithContent>
          )}
          {!mobile && renderDefaultLabel(is_default, styles)}
        </div>
      );
    },
    [mobile, getDepartmentLabel, renderDefaultLabel, userDepartmentId],
  );

  return {
    getDepartmentLabel,
    renderDefaultLabel,
    getDepartmentsInfo,
  };
};
